import { useState, useEffect } from 'react'
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    MenuItem,
    Card,
    CardContent
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import { addDoc, collection, serverTimestamp, getDocs, doc, updateDoc, increment } from 'firebase/firestore'
import { db } from '../../../config/firebase'
import { Product, PurchaseItem } from '@starwave/shared/src/types'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

export default function NewPurchasePage() {
    const navigate = useNavigate()
    const [products, setProducts] = useState<Product[]>([])

    // Form State
    const [supplierName, setSupplierName] = useState('')
    const [invoiceNumber, setInvoiceNumber] = useState('')
    const [cartItems, setCartItems] = useState<PurchaseItem[]>([])

    // Item Form
    const [selectedProductId, setSelectedProductId] = useState('')
    const [quantity, setQuantity] = useState(1)
    const [unitCost, setUnitCost] = useState(0)

    useEffect(() => {
        fetchProducts()
    }, [])

    const fetchProducts = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'products'))
            const productsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product))
            setProducts(productsData)
        } catch (error) {
            toast.error('Failed to load products')
        }
    }

    const handleAddItem = () => {
        if (!selectedProductId) return
        const product = products.find(p => p.id === selectedProductId)
        if (!product) return

        const newItem: PurchaseItem = {
            productId: product.id,
            productName: product.name,
            category: typeof product.category === 'object' ? product.category.main : product.category,
            quantity: Number(quantity),
            unitCost: Number(unitCost),
            totalCost: Number(quantity) * Number(unitCost)
        }

        setCartItems([...cartItems, newItem])
        setSelectedProductId('')
        setQuantity(1)
        setUnitCost(0)
    }

    const handleRemoveItem = (index: number) => {
        const newItems = [...cartItems]
        newItems.splice(index, 1)
        setCartItems(newItems)
    }

    const handleSubmit = async () => {
        if (!supplierName || cartItems.length === 0) {
            toast.error('Please fill in supplier details and adds items')
            return
        }

        try {
            const totalAmount = cartItems.reduce((acc, item) => acc + item.totalCost, 0)

            // 1. Record Purchase
            await addDoc(collection(db, 'purchases'), {
                supplierName,
                invoiceNumber,
                items: cartItems,
                totalAmount,
                date: serverTimestamp(),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: 'admin'
            })

            // 2. Update Stock & Cost Price for each product
            const updatePromises = cartItems.map(item => {
                const productRef = doc(db, 'products', item.productId)
                return updateDoc(productRef, {
                    'inventory.quantity': increment(item.quantity),
                    'inventory.available': increment(item.quantity),
                    'costPrice': item.unitCost // Updating to latest cost price. Could be average logic in future.
                })
            })

            await Promise.all(updatePromises)

            toast.success('Purchase recorded and stock updated!')
            navigate('/admin/purchases')

        } catch (error) {
            console.error('Error recording purchase:', error)
            toast.error('Failed to save purchase')
        }
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
                New Purchase (Stock In)
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Supplier Details</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Supplier Name"
                                    fullWidth
                                    value={supplierName}
                                    onChange={(e) => setSupplierName(e.target.value)}
                                    required
                                />
                                <TextField
                                    label="Supplier Invoice #"
                                    fullWidth
                                    value={invoiceNumber}
                                    onChange={(e) => setInvoiceNumber(e.target.value)}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Add Product</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    select
                                    label="Select Product"
                                    fullWidth
                                    value={selectedProductId}
                                    onChange={(e) => setSelectedProductId(e.target.value)}
                                >
                                    {products.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Quantity"
                                    type="number"
                                    fullWidth
                                    value={quantity}
                                    onChange={(e) => setQuantity(Number(e.target.value))}
                                    InputProps={{ inputProps: { min: 1 } }}
                                />
                                <TextField
                                    label="Unit Cost (PKR)"
                                    type="number"
                                    fullWidth
                                    value={unitCost}
                                    onChange={(e) => setUnitCost(Number(e.target.value))}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddItem}
                                    disabled={!selectedProductId}
                                >
                                    Add to List
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Items</Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product</TableCell>
                                        <TableCell align="right">Cost</TableCell>
                                        <TableCell align="right">Qty</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cartItems.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{item.productName}</TableCell>
                                            <TableCell align="right">{item.unitCost.toLocaleString()}</TableCell>
                                            <TableCell align="right">{item.quantity}</TableCell>
                                            <TableCell align="right">{item.totalCost.toLocaleString()}</TableCell>
                                            <TableCell align="right">
                                                <IconButton color="error" size="small" onClick={() => handleRemoveItem(index)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Total Payable:</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                                            PKR {cartItems.reduce((acc, i) => acc + i.totalCost, 0).toLocaleString()}
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<SaveIcon />}
                                onClick={handleSubmit}
                                disabled={cartItems.length === 0}
                            >
                                Complete Purchase
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    )
}
